import * as errors from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'

import type { Rule } from './rule'

// TODO: missing a ton of comments, particularly jsdoc

/**
 * 1 = 100%
 * 0.1 = 10%
 * 0.01 = 1%
 * 0.001 = 0.1%
 */
const MAX_ALLOWED_DELTA_FOR_100_PERCENT_MAPPING = 0.001

export interface Mapping {
  adjustmentLabel?: string
  entries: Mapping.Entry[]
}
export namespace Mapping {
  export const isMapping = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Mapping>({
      adjustmentLabel: TypeChecker.Factory.optional(TypeChecker.isString),
      entries: TypeChecker.Factory.list(Entry.isEntry),
    }),
  )

  /**
   * Default value for the exclusion label. This is used in cases where the
   * mapping contains deferred exclusions but the exclusion label is not set
   */
  const DEFAULT_EXCLUSION_LABEL = 'Other Exclusions'

  /**
   * Combines multiple {@link Mapping} into a single {@link Mapping}
   *
   * __NOTE:__ the {@link mappings} list must be ordered by priority
   *
   * This is primarily used with the concept of "deferred exclusions," which is
   * where the highest priority mapping has an exclusion and no IDC
   * categorization, which means the next highest priorit mapping that has no
   * exclusions needs to be used in order to determine how to functionalize the
   * exclusion from the first mapping
   */
  export function combine(
    ...mappings: readonly Readonly<Mapping>[]
  ): Mapping | undefined {
    // Destructuring the mappings array to extract the highest priority mapping
    // (first element assuming that the list is already ordered by priority)
    const [appliedMapping, ...lowerPriorityMappings] = mappings
    if (!appliedMapping) {
      // If no mappings are provided, return undefined
      return undefined
    }

    // Check if the highest priority mapping contains any entry that has no IDC
    // categorization (deferred exclusions)
    const hasNoFaCategorization = appliedMapping.entries.some(
      entry => !Mapping.Entry.hasIdcCategorization(entry),
    )

    if (!hasNoFaCategorization) {
      // If no deferred exclusions exist, we return the highest priority mapping
      // as-is
      return appliedMapping
    }

    // Find the next highest priority mapping (if any) that has no exclusions
    const mappingWithNoExclusions = lowerPriorityMappings.find(
      mapping => !hasExclusions(mapping),
    )

    // If no valid mapping without exclusions is found, return the
    // appliedMapping as-is
    if (!mappingWithNoExclusions) {
      return appliedMapping
    }

    // Process the entries of the highest priority mapping, adjusting deferred
    // exclusions
    const entries = appliedMapping.entries.flatMap(entry => {
      if (Mapping.Entry.hasIdcCategorization(entry)) {
        // If the entry is not a deferred exclusion, return it as-is
        return entry
      }
      // For deferred exclusions, distribute their percentages based on the next
      // valid mapping
      return mappingWithNoExclusions.entries.map(nonExcludedEntry => ({
        // In case there is no exclusion label we're going to set the default
        // value for exclusion label
        exclusionLabel: entry.exclusionLabel ?? DEFAULT_EXCLUSION_LABEL,
        faFunctionCategorization: nonExcludedEntry.faFunctionCategorization,
        // Calculate the percentage
        percent: nonExcludedEntry.percent * entry.percent,
      }))
    })

    // Return the mapping with updated entries
    return {
      ...appliedMapping,
      entries,
    }
  }

  export function stringify(mapping: Mapping): string {
    return JSON.stringify({
      adjustmentLabel: mapping.adjustmentLabel,
      entries: mapping.entries
        .map(entry => Entry.stringify(entry))
        .sort()
        .join('|'),
    } satisfies Record<keyof Mapping, string | undefined>)
  }

  export function hasExclusions(mapping: Mapping): boolean {
    return mapping.entries.some(entry => entry.exclusionLabel !== undefined)
  }

  export function getTotalPercent(mapping: Mapping): number {
    return mapping.entries.reduce((prev, entry) => prev + entry.percent, 0)
  }
  export function percentIsApproximately(
    mapping: Mapping,
    /**
     * 1 = 100%
     */
    percent: number,
  ): boolean {
    const totalPercent = getTotalPercent(mapping)
    return (
      totalPercent > percent - MAX_ALLOWED_DELTA_FOR_100_PERCENT_MAPPING &&
      totalPercent < percent + MAX_ALLOWED_DELTA_FOR_100_PERCENT_MAPPING
    )
  }
  export function assertIs100Percent(mapping: Mapping): void {
    if (!percentIsApproximately(mapping, 1)) {
      const totalPercent = getTotalPercent(mapping)
      throw new errors.UserFriendlyError(
        `Given mapping sums to ${totalPercent * 100}%. It must sum to 100%.`,
      )
    }
  }
  /**
   * Recalculates the percents in a {@link Mapping} to ensure they total 100%
   */
  export function scaleTo100Percent(mapping: Mapping): Mapping {
    if (percentIsApproximately(mapping, 1)) {
      // NOTE: this ensures we avoid slightly changing numbers due to rounding
      // errors if the mapping is already at 100%
      return mapping
    }
    const totalPercent = getTotalPercent(mapping)
    if (percentIsApproximately(mapping, 0)) {
      throw new errors.UserFriendlyError(
        `Cannot normalize mapping percents when they total 0%`,
      )
    }
    return {
      ...mapping,
      entries: mapping.entries.map(entry => ({
        ...entry,
        percent: entry.percent / totalPercent,
      })),
    }
  }

  /**
   * Represents some percentage of an overall {@link Mapping}
   */
  export interface Entry {
    // TODO: making this just IDs would simplify things, if possible
    faFunctionCategorization?: common.FaFunction.Categorization.Identifiable
    exclusionLabel?: string
    percent: number
  }
  export namespace Entry {
    export const isEntry = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Entry>({
        exclusionLabel: TypeChecker.Factory.optional(TypeChecker.isString),
        faFunctionCategorization: TypeChecker.Factory.optional(
          common.FaFunction.Categorization.Identifiable.isIdentifier,
        ),
        percent: TypeChecker.isNumber,
      }),
    )

    /**
     * Helper function to check if a given entry is has a
     * faFunctionCategorization defined
     */
    export function hasIdcCategorization(entry: Entry): boolean {
      return TypeChecker.isNotNullish(entry.faFunctionCategorization)
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function stringify({
      exclusionLabel,
      percent,
      faFunctionCategorization,
    }: Entry): string {
      return JSON.stringify({
        exclusionLabel,
        faFunctionCategorization: faFunctionCategorization
          ? common.FaFunction.Categorization.Identifiable.stringify(
              faFunctionCategorization,
            )
          : undefined,
        // NOTE: this avoids discrepancies from rounding errors
        percent: percent.toFixed(3),
      } satisfies Record<keyof Entry, string | undefined>)
    }

    export function from(source: from.Source.Guaranteed): Entry[]
    export function from(source: from.Source.NotGuaranteed): Entry[] | undefined
    export function from(source: from.Source): Entry[] | undefined {
      if (isMapping(source)) {
        return source.entries
      }
      return source.mapping.entries
    }
    export namespace from {
      export type Source = Source.Guaranteed | Source.NotGuaranteed
      export namespace Source {
        export type Guaranteed = Mapping
        export type NotGuaranteed = Rule
      }
    }
  }
}
